import { Injectable, OnDestroy } from '@angular/core';
import type { AdditionalInfoFieldKey } from '@modules/rx-intuitive-crown-additional-info/models/additional-info';
import { AdditionalInfoResult } from '@modules/rx-intuitive-crown-additional-info/models/additional-info';
import { CrownForm } from '@modules/rx-intuitive-crown/models/crown-form';
import { IntuitiveToothForTreatment } from './models';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { RxIntuitiveTeethHelperService } from './services/rx-intuitive-teeth-helper.service';
import { RxIntuitiveToothEditorRoter } from './services/rx-intuitive-tooth-editor-roter.service';
import { IntuitiveIndication, toUnitTypeOrDefault } from './models/intuitive-indication';
import { ToothModalResult } from './models/tooth-modal';
import { RxIntuitiveIndicationMapperService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-indication-mapper.service';
import { IntuitiveBridge } from './models/intuitive-bridge';
import { RxIntuitiveBridgeService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-bridge-service';
import { RxIntuitiveTeethDiagramStore } from '@modules/rx-intuitive-teeth-orchestrator/state/rx-intuitive-teeth-diagram-store';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { RxIntuitiveTeethDiagramQuery } from '@modules/rx-intuitive-teeth-orchestrator/state/rx-intuitive-teeth-diagram-query';
import { RxIntuitiveIndicationRulesService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-indication-rules.service';
import { RxIntuitiveToothEditorReadOnlyService } from './services/tooth-editor/rx-intuitive-tooth-editor-read-only.service';
import { RxIntuitiveToothEditorMutationService } from './services/tooth-editor/rx-intuitive-tooth-editor-mutation.service';
import { RxIntuitiveToothEditorViewService } from './services/tooth-editor/rx-intuitive-tooth-editor-view.service';
import { IRxIntuitiveToothEditorFacade } from './models/rx-intuitive-tooth-editor-interface.facade';

@Injectable()
export class RxIntuitiveToothEditorFacade implements IRxIntuitiveToothEditorFacade, OnDestroy {
	readonly facadeName: string = 'RxIntuitiveToothEditorFacade';

	// ── Read-only / permission ────────────────────────────────────────────────
	readonly toothEditorReadOnly$: Observable<boolean> = this.readOnlyService.toothEditorReadOnly$;
	readonly canEditToothEditor$: Observable<boolean> = this.readOnlyService.canEditToothEditor$;

	/** Delete control in the tooth editor header — off in shell read-only. */
	readonly canDeleteToothEditor$: Observable<boolean> = combineLatest([
		this.viewService.isToothEditorShown$,
		this.readOnlyService.toothEditorReadOnly$
	]).pipe(map(([editorShown, readOnly]) => editorShown && !readOnly));

	// ── View / query ─────────────────────────────────────────────────────────
	readonly teethNumberingSystem$ = this.viewService.teethNumberingSystem$;
	readonly isToothEditorShown$: Observable<boolean> = this.viewService.isToothEditorShown$;
	readonly teethFlat$ = this.viewService.teethFlat$;
	readonly editorTeeth$: Observable<IntuitiveToothForTreatment[]> = this.viewService.editorTeeth$;
	readonly previewTreatmentListItems$ = this.viewService.previewTreatmentListItems$;

	// ── Lifecycle ─────────────────────────────────────────────────────────────
	readonly facadeDestroy$ = new Subject<void>();

	constructor(
		protected readonly readOnlyService: RxIntuitiveToothEditorReadOnlyService,
		protected readonly mutationService: RxIntuitiveToothEditorMutationService,
		protected readonly viewService: RxIntuitiveToothEditorViewService,
		protected readonly teethHelper: RxIntuitiveTeethHelperService,
		protected readonly editorRouting: RxIntuitiveToothEditorRoter,
		protected readonly indicationMapper: RxIntuitiveIndicationMapperService,
		protected readonly bridgeService: RxIntuitiveBridgeService,
		protected readonly intuitiveStore: RxIntuitiveTeethDiagramStore,
		protected readonly intuitiveQuery: RxIntuitiveTeethDiagramQuery,
		protected readonly teethDiagramQuery: TeethDiagramQuery,
		protected readonly indicationsService: RxIntuitiveIndicationRulesService
	) {}

	ngOnDestroy(): void {
		this.facadeDestroy$.next();
		this.facadeDestroy$.complete();
	}

	get shouldValidateForSend(): boolean {
		return this.readOnlyService.shouldValidateForSend;
	}

	/**
	 * Opens the appropriate tooth modal based on the unit type.
	 * Returns an Observable that emits the modal result, or null if no modal is
	 * needed for this indication.
	 */
	openModalForUnitTypeIfNeeded(indication: IntuitiveIndication): Observable<ToothModalResult> | null {
		return this.editorRouting.openModalForIndicationIfNeeded(indication, {
			onSave: (toothIds, partialUpdate, indicationId) => {
				this.mutationService.updateTeethByIdsForIndicationWithModal(
					toothIds,
					partialUpdate,
					toUnitTypeOrDefault(indicationId, this.indicationMapper.getDefaultUnitType())
				);
			},
			onDelete: toothIds => {
				this.mutationService.resetIndicationForTeeth(toothIds);
			},
			onClearedFields: (toothIds, clearedFields) => {
				this.mutationService.markCrownControlsAsRemovedByUser(toothIds, clearedFields as AdditionalInfoFieldKey[]);
			}
		});
	}

	openTreatmentEditorFromList(toothId: number): void {
		const teethRecord = this.teethHelper.teethRecord;
		const tooth = this.teethHelper.getToothById(toothId, teethRecord);

		if (tooth?.BridgeIndex) {
			const bridge = this.bridgeService.getBridgeSpanByBridgeIndex(tooth.BridgeIndex, this.teethDiagramQuery.teethFlat);

			if (bridge) {
				return this.selectBridge(bridge, toothId);
			}
		}

		if (tooth?.UnitTypeID) {
			this.updateSelectedToothIds([toothId]);

			const modalResult$ = this.openModalForUnitTypeIfNeeded(
				this.indicationMapper.getUnitTypeIndicationForDiagram(tooth.UnitTypeID)
			);

			if (modalResult$) {
				modalResult$.pipe(takeUntil(this.facadeDestroy$)).subscribe();
			}
		}
		// Otherwise the side editor appears reactively via isToothEditorShown$
	}

	resetIndicationForSelectedTeeth(): void {
		const teeth = this.teethDiagramQuery.teethRecord;
		const selectedToothIds = this.intuitiveQuery.selectedToothIds;
		const toothIdsToReset = selectedToothIds.filter(id => !this.indicationMapper.isDefaultUnitType(teeth[id]?.UnitTypeID));

		this.mutationService.resetIndicationForTeeth(toothIdsToReset);

		if (toothIdsToReset.length > 0) {
			this.updateSelectedToothIds([]);
		}
	}

	resetIndicationForTeeth(toothIdsToReset: number[]): void {
		this.mutationService.resetIndicationForTeeth(toothIdsToReset);
	}

	updateToothCrownData(crownForm: CrownForm): void {
		this.mutationService.updateToothCrownData(crownForm);
	}

	applyToothAdditionalInfo(toothIds: number[], result: AdditionalInfoResult): void {
		this.mutationService.applyToothAdditionalInfo(toothIds, result);
	}

	clearDisabledToothFields(toothIds: number[], payload: Partial<Record<string, unknown>>): void {
		this.mutationService.clearDisabledToothFields(toothIds, payload);
	}

	hasAdditionalInfoButtonSaved(teethFlat: readonly Tooth[], sessionToothIds: readonly number[]): boolean {
		return this.viewService.hasAdditionalInfoButtonSaved(teethFlat, sessionToothIds);
	}

	selectBridge(bridge: IntuitiveBridge, focusOnTooth?: number): void {
		this.intuitiveStore.selectBridge(bridge, focusOnTooth);
	}

	deselectBridge(): void {
		this.intuitiveStore.deselectBridge();
	}

	shouldShowToothEditorProperties(indicationId: number): boolean {
		return this.indicationsService.shouldShowToothEditorProperties({ indicationId });
	}

	private updateSelectedToothIds(toothIds: number[]): void {
		this.intuitiveStore.update({ selectedToothIds: toothIds });
	}
}
